import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { fetchBranches } from "../../../api/branches";
import {
    fetchAcademyRevenueSummary,
    exportAcademyRevenueSummary,
    emailAcademyRevenueSummary,
} from "../../../api/adminReports";
import DatePicker from "../../../components/common/DatePicker";
import ReportExportBar from "../../../components/reports/ReportExportBar";
import ExcelDownloadButton from "../../../components/common/ExcelDownloadButton";
import { exportMultiSheet } from "../../../utils/excelExport";
import ReportGenerateButton from "../../../components/reports/ReportGenerateButton";
import { formatCurrency as fmt } from "../../../utils/currency";
import { SelectInput } from "../../../components/common/form";
import PaginationFooter from "../../../components/common/PaginationFooter";
import SortableTableHeader from "../../../components/common/SortableTableHeader";
import useSortedClientPagination from "../../../hooks/useSortedClientPagination";
import {
    academyRevenueMonthlySortColumns,
    academyRevenueBranchSortColumns,
} from "../../../config/tableSortColumns";
import {
    ChartPanel,
    TrendLine,
    CompareBar,
    CompositionDonut,
    CHART_COLORS,
} from "../../../components/charts";
import {
    firstDayOfMonthRegional,
    todayRegionalDate,
    toISODateFromRegional,
} from "../../../utils/date";
import { formatMonthYearFromSortKey } from "../../../utils/regionalFormat";
import { apiErrorMessage } from '../../../utils/apiError';

function getFirstDayOfMonth() {
    return firstDayOfMonthRegional();
}
function getToday() {
    return todayRegionalDate();
}

function monthLabel(row) {
    return formatMonthYearFromSortKey(row?.sortKey) || row?.month || '';
}
function ddmmyyyyToIso(str) {
    const iso = toISODateFromRegional(str);
    return iso || null;
}

// ─── Download helper ─────────────────────────────────────────────────────────
async function triggerDownload(blobReq, filename) {
    const res = await blobReq;
    const blob = new Blob([res.data], {
        type: res.headers["content-type"] || "application/octet-stream",
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
}

export default function AcademyRevenueSummaryReport() {
    const { t, i18n } = useTranslation();
    const [from, setFrom] = useState(getFirstDayOfMonth());
    const [to, setTo] = useState(getToday());
    const [branchId, setBranchId] = useState("");
    const [branches, setBranches] = useState([]);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");
    const [hasRun, setHasRun] = useState(false);
    const [filtersLoadError, setFiltersLoadError] = useState(false);

    const monthlyRows = data?.monthlyRows ?? [];
    const branchRows = data?.branchRows ?? [];
    const reportKey = `${from}-${to}-${branchId}-${monthlyRows.length}-${branchRows.length}`;
    const monthlyPagination = useSortedClientPagination(monthlyRows, {
        columns: academyRevenueMonthlySortColumns,
        resetKey: reportKey,
    });
    const branchPagination = useSortedClientPagination(branchRows, {
        columns: academyRevenueBranchSortColumns,
        resetKey: reportKey,
    });

    useEffect(() => {
        fetchBranches()
            .then((res) => {
                setBranches(res?.data || []);
                setFiltersLoadError(false);
            })
            .catch(() => {
                setBranches([]);
                setFiltersLoadError(true);
            });
    }, []);

    const generate = async () => {
        const fromIso = ddmmyyyyToIso(from);
        const toIso = ddmmyyyyToIso(to);
        if (!fromIso || !toIso) {
            setErr(t("reports.common.invalidDateRange"));
            return;
        }
        setErr("");
        setLoading(true);
        setHasRun(true);
        try {
            const res = await fetchAcademyRevenueSummary({
                from: fromIso,
                to: toIso,
                branchId: branchId || undefined,
            });
            setData(res.data);
        } catch (e) {
            setErr(apiErrorMessage(e, t("reports.common.failedToLoad")));
            setData(null);
        } finally {
            setLoading(false);
        }
    };

    const fromIso = ddmmyyyyToIso(from);
    const toIso = ddmmyyyyToIso(to);
    const exportParams = { from: fromIso, to: toIso, branchId: branchId || undefined };

    const handleExportPdf = () =>
        triggerDownload(
            exportAcademyRevenueSummary({ ...exportParams, format: "pdf" }),
            "academy-revenue-summary.pdf"
        );

    const handleExportCsv = () =>
        triggerDownload(
            exportAcademyRevenueSummary({ ...exportParams, format: "csv" }),
            "academy-revenue-summary.csv"
        );

    const handleSendEmail = (email) =>
        emailAcademyRevenueSummary({ ...exportParams, format: "pdf", toEmail: email });

    const s = data?.summary;

    const monthlyChartData = useMemo(() => {
        if (!monthlyRows.length) return [];
        return monthlyRows.map((row) => ({
            name: monthLabel(row),
            gross: row.gross,
            academyRevenue: row.academyRevenue,
            coachPayouts: row.coachPayouts,
        }));
    }, [monthlyRows, i18n.language]);

    const monthlyHasSignal = useMemo(
        () => monthlyChartData.some((r) => Number(r.gross) || Number(r.academyRevenue) || Number(r.coachPayouts)),
        [monthlyChartData],
    );

    const monthlySeries = useMemo(() => [
        {
            dataKey: "gross",
            name: t("reports.academyRevenue.grossRevenue", { defaultValue: "Gross revenue" }),
            color: CHART_COLORS.accent,
        },
        {
            dataKey: "academyRevenue",
            name: t("reports.academyRevenue.academyRevenue", { defaultValue: "Academy revenue" }),
            color: CHART_COLORS.primary,
        },
        {
            dataKey: "coachPayouts",
            name: t("reports.academyRevenue.coachPayout", { defaultValue: "Coach payout" }),
            color: CHART_COLORS.warning,
        },
    ], [t]);

    const branchChartData = useMemo(() => {
        if (!branchRows.length) return [];
        return branchRows
            .map((row) => ({
                name: row.branchName || t("reports.common.unknown", { defaultValue: "Unknown" }),
                value: Number(row.gross) || 0,
            }))
            .filter((d) => d.value > 0);
    }, [branchRows, t]);

    const revenueSplitData = useMemo(() => {
        if (!s) return [];
        return [
            {
                name: t("reports.academyRevenue.academyRevenue", { defaultValue: "Academy" }),
                value: Number(s.totalAcademyRevenue) || 0,
                color: CHART_COLORS.primary,
            },
            {
                name: t("reports.academyRevenue.coachPayout", { defaultValue: "Coach" }),
                value: Number(s.totalCoachPayouts) || 0,
                color: CHART_COLORS.warning,
            },
        ].filter((d) => d.value > 0);
    }, [s, t]);

    return (
        <div className="p-6 space-y-6">
            <p className="text-sm text-gray-600">
                {t("reports.academyRevenue.subtitle")}
            </p>

            <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-800 flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                    </svg>
                    {t("reports.common.filters")}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                    <div>
                        <label className="text-xs font-semibold text-gray-700 mb-2 block">📅 {t("reports.common.fromDate")}</label>
                        <DatePicker value={from} onChange={setFrom} className="w-full" />
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-gray-700 mb-2 block">📅 {t("reports.common.toDate")}</label>
                        <DatePicker value={to} onChange={setTo} className="w-full" />
                    </div>
                    <SelectInput
                        label={t("reports.common.filterByBranch")}
                        value={branchId}
                        onChange={(e) => setBranchId(e.target.value)}
                    >
                        <option value="">{t("reports.common.allBranches")}</option>
                        {branches.map((b) => (
                            <option key={b.id} value={b.id}>{b.name}</option>
                        ))}
                    </SelectInput>
                    <div>
                        <ReportGenerateButton
                            onClick={generate}
                            loading={loading}
                            label={t("reports.common.generateReport")}
                            loadingLabel={t("reports.common.loadingReport")}
                        />
                    </div>
                </div>
            </div>

            {filtersLoadError && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" role="status">{t("common.optionsLoadFailed")}</div>
            )}
            {err && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">{err}</div>
            )}

            {hasRun && !loading && (!monthlyChartData.length || !monthlyHasSignal) && revenueSplitData.length === 0 && branchChartData.length === 0 ? (
                <ChartPanel
                    title={t("charts.academyMonthlyTrend", { defaultValue: "Monthly revenue trend" })}
                    empty
                    emptyMessage={t("charts.noMonthlyData", { defaultValue: "No revenue data to chart for this period" })}
                />
            ) : hasRun ? (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <ChartPanel
                        title={t("charts.academyMonthlyTrend", { defaultValue: "Monthly revenue trend" })}
                        subtitle={t("charts.academyMonthlyTrendHint", { defaultValue: "Gross, academy, and coach payouts by month" })}
                        loading={loading}
                        empty={!loading && (!monthlyChartData.length || !monthlyHasSignal)}
                        emptyMessage={t("charts.noMonthlyData", { defaultValue: "No monthly breakdown to chart" })}
                        error={err || null}
                        height={240}
                        className="lg:col-span-2"
                    >
                        <TrendLine
                            data={monthlyChartData}
                            series={monthlySeries}
                            height={220}
                            yFormatter={(v) => fmt(v)}
                        />
                    </ChartPanel>
                    <ChartPanel
                        title={t("charts.academyRevenueSplit", { defaultValue: "Revenue split" })}
                        subtitle={t("charts.academyRevenueSplitHint", { defaultValue: "Academy vs coach payout" })}
                        loading={loading}
                        empty={!loading && revenueSplitData.length === 0}
                        emptyMessage={t("charts.noSplitData", { defaultValue: "No commission split to chart" })}
                        error={err || null}
                        height={240}
                    >
                        <CompositionDonut
                            data={revenueSplitData}
                            chartSize={180}
                            valueFormatter={(v) => fmt(v)}
                        />
                    </ChartPanel>
                    <ChartPanel
                        title={t("charts.academyByBranch", { defaultValue: "Gross by branch" })}
                        subtitle={t("charts.academyByBranchHint", { defaultValue: "Compare branch gross revenue" })}
                        loading={loading}
                        empty={!loading && branchChartData.length === 0}
                        emptyMessage={t("charts.noBranchData", { defaultValue: "No branch enrollment data yet" })}
                        error={err || null}
                        height={Math.max(240, 48 + branchChartData.length * 36)}
                        className="lg:col-span-3"
                    >
                        <CompareBar
                            data={branchChartData}
                            layout="horizontal"
                            height={Math.max(220, branchChartData.length * 36)}
                            yFormatter={(v) => fmt(v)}
                        />
                    </ChartPanel>
                </div>
            ) : null}

            {!loading && s && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg shadow-md p-4 text-white">
                        <div className="text-xs opacity-90 mb-1">{t("reports.coachRevenue.totalSessions")}</div>
                        <div className="text-3xl font-bold">{s.totalSessions}</div>
                        <div className="text-xs opacity-75 mt-1">
                            {t("reports.coachRevenue.sessionMix", {
                                individual: s.totalIndividualSessions,
                                batch: s.totalBatchSessions,
                            })}
                        </div>
                    </div>
                    <div className="bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg shadow-md p-4 text-white">
                        <div className="text-xs opacity-90 mb-1">{t("reports.academyRevenue.grossRevenue")}</div>
                        <div className="text-xl font-bold">{fmt(s.totalGrossRevenue)}</div>
                    </div>
                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-md p-4 text-white">
                        <div className="text-xs opacity-90 mb-1">{t("reports.academyRevenue.academyRevenue")}</div>
                        <div className="text-xl font-bold">{fmt(s.totalAcademyRevenue)}</div>
                    </div>
                    <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg shadow-md p-4 text-white">
                        <div className="text-xs opacity-90 mb-1">{t("reports.academyRevenue.coachPayout")}</div>
                        <div className="text-xl font-bold">{fmt(s.totalCoachPayouts)}</div>
                    </div>
                    <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg shadow-md p-4 text-white">
                        <div className="text-xs opacity-90 mb-1">{t("reports.academyRevenue.walletTopUps")}</div>
                        <div className="text-xl font-bold">{fmt(s.walletTopUpsCollected)}</div>
                    </div>
                    <div className="bg-gradient-to-br from-slate-500 to-slate-600 rounded-lg shadow-md p-4 text-white">
                        <div className="text-xs opacity-90 mb-1">{t("reports.academyRevenue.walletBalanceHeld")}</div>
                        <div className="text-xl font-bold">{fmt(s.totalWalletBalance)}</div>
                    </div>
                </div>
            )}

            {!loading && data?.monthlyRows?.length > 0 && (
                <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
                    <div className="px-6 py-4 border-b bg-gray-50">
                        <h3 className="text-base font-semibold text-gray-800">📅 {t("reports.academyRevenue.monthlyBreakdown")}</h3>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-gray-700 text-white">
                                    <SortableTableHeader label={t("reports.academyRevenue.month")} columnKey="month" sortKey={monthlyPagination.sortKey} sortDir={monthlyPagination.sortDir} onSort={monthlyPagination.toggleSort} className="px-4 py-3 text-center font-semibold" align="center" />
                                    <SortableTableHeader label={t("reports.academyRevenue.sessions")} columnKey="sessions" sortKey={monthlyPagination.sortKey} sortDir={monthlyPagination.sortDir} onSort={monthlyPagination.toggleSort} className="px-4 py-3 text-center font-semibold" align="center" />
                                    <SortableTableHeader label={t("reports.academyRevenue.grossRevenue")} columnKey="gross" sortKey={monthlyPagination.sortKey} sortDir={monthlyPagination.sortDir} onSort={monthlyPagination.toggleSort} className="px-4 py-3 text-center font-semibold" align="center" />
                                    <SortableTableHeader label={t("reports.academyRevenue.academyRevenue")} columnKey="academyRevenue" sortKey={monthlyPagination.sortKey} sortDir={monthlyPagination.sortDir} onSort={monthlyPagination.toggleSort} className="px-4 py-3 text-center font-semibold" align="center" />
                                    <SortableTableHeader label={t("reports.academyRevenue.coachPayout")} columnKey="coachPayouts" sortKey={monthlyPagination.sortKey} sortDir={monthlyPagination.sortDir} onSort={monthlyPagination.toggleSort} className="px-4 py-3 text-center font-semibold" align="center" />
                                    <SortableTableHeader label={t("reports.academyRevenue.walletTopUps")} columnKey="walletTopUps" sortKey={monthlyPagination.sortKey} sortDir={monthlyPagination.sortDir} onSort={monthlyPagination.toggleSort} className="px-4 py-3 text-center font-semibold" align="center" />
                                </tr>
                            </thead>
                            <tbody>
                                {monthlyPagination.pageItems.map((row, i) => (
                                    <tr key={row.sortKey} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                                        <td className="px-4 py-3 font-medium text-gray-800">{monthLabel(row)}</td>
                                        <td className="px-4 py-3 text-center text-gray-700">{row.sessionCount}</td>
                                        <td className="px-4 py-3 text-center text-gray-700">{fmt(row.gross)}</td>
                                        <td className="px-4 py-3 text-center text-blue-700 font-medium">{fmt(row.academyRevenue)}</td>
                                        <td className="px-4 py-3 text-center text-orange-700">{fmt(row.coachPayouts)}</td>
                                        <td className="px-4 py-3 text-center text-teal-700">{fmt(row.walletTopUps)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {monthlyPagination.total > 0 && (
                        <PaginationFooter
                            page={monthlyPagination.page}
                            size={monthlyPagination.size}
                            total={monthlyPagination.total}
                            onPageChange={monthlyPagination.setPage}
                            onSizeChange={monthlyPagination.setSize}
                        />
                    )}
                </div>
            )}

            {!loading && data?.branchRows?.length > 0 && (
                <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
                    <div className="px-6 py-4 border-b bg-gray-50">
                        <h3 className="text-base font-semibold text-gray-800">🏢 {t("reports.academyRevenue.branchBreakdown")}</h3>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-gray-700 text-white">
                                    <SortableTableHeader label={t("reports.common.branch")} columnKey="branch" sortKey={branchPagination.sortKey} sortDir={branchPagination.sortDir} onSort={branchPagination.toggleSort} className="px-4 py-3 text-center font-semibold" align="center" />
                                    <SortableTableHeader label={t("reports.academyRevenue.sessions")} columnKey="sessions" sortKey={branchPagination.sortKey} sortDir={branchPagination.sortDir} onSort={branchPagination.toggleSort} className="px-4 py-3 text-center font-semibold" align="center" />
                                    <SortableTableHeader label={t("reports.academyRevenue.grossRevenue")} columnKey="gross" sortKey={branchPagination.sortKey} sortDir={branchPagination.sortDir} onSort={branchPagination.toggleSort} className="px-4 py-3 text-center font-semibold" align="center" />
                                    <SortableTableHeader label={t("reports.academyRevenue.academyRevenue")} columnKey="academyRevenue" sortKey={branchPagination.sortKey} sortDir={branchPagination.sortDir} onSort={branchPagination.toggleSort} className="px-4 py-3 text-center font-semibold" align="center" />
                                    <SortableTableHeader label={t("reports.academyRevenue.coachPayout")} columnKey="coachPayouts" sortKey={branchPagination.sortKey} sortDir={branchPagination.sortDir} onSort={branchPagination.toggleSort} className="px-4 py-3 text-center font-semibold" align="center" />
                                </tr>
                            </thead>
                            <tbody>
                                {branchPagination.pageItems.map((row, i) => (
                                    <tr key={row.branchId} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                                        <td className="px-4 py-3 font-medium text-gray-800">{row.branchName}</td>
                                        <td className="px-4 py-3 text-center text-gray-700">{row.sessionCount}</td>
                                        <td className="px-4 py-3 text-center text-gray-700">{fmt(row.gross)}</td>
                                        <td className="px-4 py-3 text-center text-blue-700 font-medium">{fmt(row.academyRevenue)}</td>
                                        <td className="px-4 py-3 text-center text-orange-700">{fmt(row.coachPayouts)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {branchPagination.total > 0 && (
                        <PaginationFooter
                            page={branchPagination.page}
                            size={branchPagination.size}
                            total={branchPagination.total}
                            onPageChange={branchPagination.setPage}
                            onSizeChange={branchPagination.setSize}
                        />
                    )}
                </div>
            )}

            {!loading && hasRun && data && !data.monthlyRows?.length && (
                <div className="text-center py-16 text-gray-400">
                    <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="font-medium">{t("reports.academyRevenue.empty")}</p>
                </div>
            )}

            <div className="flex flex-wrap items-center gap-3">
                <ExcelDownloadButton
                    onExport={() => {
                        const sheets = [];
                        if (monthlyRows.length > 0) {
                            sheets.push({
                                name: t("reports.academyRevenue.monthlyBreakdown"),
                                columns: [
                                    { header: t("reports.academyRevenue.month"), key: "month", width: 16 },
                                    { header: t("reports.academyRevenue.sessions"), key: "sessionCount", width: 10 },
                                    { header: t("reports.academyRevenue.grossRevenue"), key: "gross", render: (v) => fmt(v || 0), width: 14 },
                                    { header: t("reports.academyRevenue.academyRevenue"), key: "academyRevenue", render: (v) => fmt(v || 0), width: 16 },
                                    { header: t("reports.academyRevenue.coachPayout"), key: "coachPayouts", render: (v) => fmt(v || 0), width: 14 },
                                    { header: t("reports.academyRevenue.walletTopUps"), key: "walletTopUps", render: (v) => fmt(v || 0), width: 14 },
                                ],
                                rows: monthlyRows.map((row) => ({ ...row, month: monthLabel(row) })),
                            });
                        }
                        if (branchRows.length > 0) {
                            sheets.push({
                                name: t("reports.academyRevenue.branchBreakdown"),
                                columns: [
                                    { header: t("reports.common.branch"), key: "branchName", width: 22 },
                                    { header: t("reports.academyRevenue.sessions"), key: "sessionCount", width: 10 },
                                    { header: t("reports.academyRevenue.grossRevenue"), key: "gross", render: (v) => fmt(v || 0), width: 14 },
                                    { header: t("reports.academyRevenue.academyRevenue"), key: "academyRevenue", render: (v) => fmt(v || 0), width: 16 },
                                    { header: t("reports.academyRevenue.coachPayout"), key: "coachPayouts", render: (v) => fmt(v || 0), width: 14 },
                                ],
                                rows: branchRows,
                            });
                        }
                        if (sheets.length > 0) exportMultiSheet(sheets, "academy-revenue-summary");
                    }}
                    filename="academy-revenue-summary"
                    disabled={!data || (monthlyRows.length === 0 && branchRows.length === 0)}
                />
                <ReportExportBar
                    disabled={!data}
                    onExportPdf={handleExportPdf}
                    onExportCsv={handleExportCsv}
                    onSendEmail={handleSendEmail}
                    emailLabel={t("reports.common.enterEmail")}
                />
            </div>
        </div>
    );
}
